/**!
 * Created by Leonardo Cheslacki on 22/06/2024.
 *
 * MIT License
 *
 * Copyright (c) 2024 cheslacki
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.

 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';

import StageController from './StageController';

import {HelpersIcon, HelpersPin} from './../helpers';

import Big from 'big.js';

import config from '../../../../config';

const {default: {orientation: {speed, hours, stations, degrees, slide}}} = config;

import WithType from './../helpers/WithType';

let _this = {};

/**
 *
 */
class StageSun extends StageController {

    /**
     *
     * @param props
     * @param main
     */
    constructor(props, main = {}) {
        super(props);
        this.props = props;
        this.main = main;

        const {current_name} = this.props;

        this.state = {};

        this.$big_frame = this.$controller.big_1;

        this.$helpersIcon = HelpersIcon(this)({current_name: current_name});
        this.$helpersPin = HelpersPin(this)({current_name: current_name});

        const {properties} = this.getComposition().getLibrary();

        this.$properties = properties;

        _this = this;
    };

    $functions = {

        /**
         *
         * @param event
         * @param _this_frame
         * @param callback
         */
        handleSlideMouseDown: function (event, {_this_frame, callback}) {
            const {stage, occ_slider: {occ_slide}} = _this_frame;

            let
                big_mouseX = (new Big(stage.mouseX)),
                big_scaleX = (new Big(stage.scaleX)),
                big_slide_x = (new Big(occ_slide.x));

            _this.setStateOccurrence(occ_slide, {big_offsetX: big_mouseX.div(big_scaleX).minus(big_slide_x)});

            (typeof (callback) === 'function') && callback(true);
        },

        /**
         *
         * @param event
         * @param _this_frame
         * @param sun
         */
        handleSlidePressMove: function (event, {_this_frame, sun}) {
            const
                {
                    stage,
                    occ_slider,
                    occ_plant: {occ_effect: {totalFrames}},
                    occ_slider: {occ_slide},
                } = _this_frame,
                {big_offsetX} = occ_slide.state;

            let
                big_mouseX = (new Big(stage.mouseX)),
                big_scaleX = (new Big(stage.scaleX)),
                big_slider_width = (new Big(occ_slider.nominalBounds.width)),
                big_slide_width = (new Big(occ_slide.nominalBounds.width)),
                big_limit = big_slide_width.div(_this.$controller.big_2),
                big_position = big_mouseX.div(big_scaleX).minus(big_offsetX);

            if (big_position.gte(big_limit) && big_position.lte(big_slider_width.minus(big_limit))) {
                let
                    original = big_slider_width.minus(big_slide_width),
                    target = big_position.minus(big_limit);

                sun.setCoordinateSun(_this.calcSize(original, target, totalFrames, false, 'int'));
                sun.setCoordinateSlide(_this.bigToNumber(big_position, false, 'int'));
            }
        },

        /**
         *
         * @param event
         * @param _this_frame
         * @param sun
         * @param callback
         */
        handleSlideTouch: function (event, {_this_frame, sun, callback}) {
            if (event.type === 'touchmove') {
                _this.stopPropagation(event);
            } else {
                (typeof (callback) === 'function') && callback(true);
            }

            const
                {width, height} = _this.$properties,
                {stage, occ_slider, occ_plant: {occ_effect: {totalFrames}}, occ_slider: {occ_slide}} = _this_frame;

            let
                enabled = false,
                /**
                 * Resized auto from scale native.
                 * @type {Touch}
                 */
                target_touch = event.targetTouches.item(0),
                big_clientX = (new Big(target_touch.clientX)),
                big_clientY = (new Big(target_touch.clientY)),
                big_scaleX = (new Big(stage.scaleX)),
                big_scaleY = (new Big(stage.scaleY)),
                /**
                 *
                 * @type {any}
                 */
                big_slider_x = (new Big(occ_slider.x)),
                big_slider_y = (new Big(occ_slider.y)),
                big_slider_width = (new Big(occ_slider.nominalBounds.width)),
                big_slider_height = (new Big(occ_slider.nominalBounds.height)),
                big_slide_width = (new Big(occ_slide.nominalBounds.width)),
                big_limit = big_slide_width.div(_this.$controller.big_2),
                /**
                 *
                 * @type {{}}
                 */
                big_leftHorizontal = {},
                big_topVertical = {},
                big_rightHorizontal = {},
                big_bottomVertical = {},
                big_positionX = {};

            if (_this.isMobile() || _this.isTablet()) {
                big_clientX = _this.calcSize(stage.canvas.clientWidth, width, big_clientX, true);
                big_clientY = _this.calcSize(stage.canvas.clientHeight, height, big_clientY, true);
                big_leftHorizontal = big_slider_x;
                big_topVertical = big_slider_y;
                big_rightHorizontal = big_leftHorizontal.plus(big_slider_width);
                big_bottomVertical = big_topVertical.plus(big_slider_height);
                big_positionX = big_clientX.minus(big_slider_x);
            } else {
                big_leftHorizontal = _this.calcSize(_this.$controller.big_1, big_scaleX, big_slider_x, true);
                big_topVertical = _this.calcSize(_this.$controller.big_1, big_scaleY, big_slider_y, true);
                big_rightHorizontal = big_leftHorizontal.plus(_this.calcSize(_this.$controller.big_1, big_scaleX, big_slider_width, true));
                big_bottomVertical = big_topVertical.plus(_this.calcSize(_this.$controller.big_1, big_scaleY, big_slider_height, true));
                big_positionX = big_clientX.div(big_scaleX).minus(big_slider_x);
            }

            // _this.testCoordinate(stage, big_clientX, big_clientY);

            if ((big_clientX.gte(big_leftHorizontal) && big_clientX.lte(big_rightHorizontal)) && (big_clientY.gte(big_topVertical) && big_clientY.lte(big_bottomVertical))) {
                enabled = true;
            }

            if (big_positionX.gte(big_limit) && big_positionX.lte(big_slider_width.minus(big_limit)) && enabled) {
                let
                    original = big_slider_width.minus(big_slide_width),
                    target = big_positionX.minus(big_limit);

                sun.setCoordinateSun(_this.calcSize(original, target, totalFrames, false, 'int'));
                sun.setCoordinateSlide(_this.bigToNumber(big_positionX, false, 'int'));
            }
        },

        /**
         *
         * @param event
         * @param _this_frame
         * @param sun
         * @param data
         * @param callback
         */
        handleSlideHours: function (event, {_this_frame, sun, data, callback}) {
            const
                {occ_plant: {occ_effect: {totalFrames}}} = _this_frame,
                {big_slider_width, big_slide_width, big_limit, big_position, big_speed} = data;

            _this.$big_frame = _this.$big_frame.plus(big_speed);

            let
                original = big_slider_width.minus(big_slide_width),
                target = _this.$big_frame.minus(big_limit);

            sun.setCoordinateSun(_this.calcSize(original, target, totalFrames, false, 'int'));
            sun.setCoordinateSlide(_this.bigToNumber(_this.$big_frame, false, 'int'));

            _this.$big_frame.gte(big_position) && ((typeof (callback) === 'function') && callback(true));
        },

        /**
         *
         * @param event
         * @param _this_frame
         * @param sun
         * @param occ
         */
        handleSlideStationMouseDown: function (event, {_this_frame, sun, occ}) {
            _this.changeStation(_this_frame, sun, occ);
        }
    };

    /**
     *
     * @param _this_frame
     * @param occ
     */
    pseudoAfter = (_this_frame, occ) => {
        const
            {
                btn_autumn,
                btn_winter,
                btn_spring,
                btn_summer,
                pseudo_autumn,
                pseudo_winter,
                pseudo_spring,
                pseudo_summer
            } = _this_frame;

        let
            autumn = (occ === 'autumn'),
            winter = (occ === 'winter'),
            spring = (occ === 'spring'),
            summer = (occ === 'summer');

        btn_autumn.visible = !autumn;
        pseudo_autumn.visible = autumn;

        btn_winter.visible = !winter;
        pseudo_winter.visible = winter;

        btn_spring.visible = !spring;
        pseudo_spring.visible = spring;

        btn_summer.visible = !summer;
        pseudo_summer.visible = summer;
    };

    /**
     *
     * @param _this_frame
     * @param sun
     * @param occ
     */
    changeStation = (_this_frame, sun, occ) => {
        const disabledAnimate = this.$helpersIcon.disabledAnimate(_this_frame);
        sun.setRotation(degrees[occ]);
        disabledAnimate(occ);
        this.pseudoAfter(_this_frame, occ);
    };

    /**
     *
     * @param _this_frame
     * @param sun
     */
    syncStation = (_this_frame, sun) => {
        let
            day = (new Big(this.$day)),
            month = stations.find((object) => (object.month === this.$month));

        /**
         *
         * @param array
         * @returns {boolean}
         */
        const checkStation = (array) => {
            if (this.inArray(array, month.month)) {
                if (this.isEmpty(month.day)) {
                    return true;
                } else if (month.month === this.firstArray(array) && day.gt(new Big(month.day))) {
                    return true;
                } else if (month.month === this.lastArray(array) && day.lte(new Big(month.day))) {
                    return true;
                }
            }
            return false;
        };

        switch (true) {
            case (checkStation([3, 4, 5, 6])):
                this.changeStation(_this_frame, sun, 'autumn');
                break;
            case (checkStation([6, 7, 8, 9])):
                this.changeStation(_this_frame, sun, 'winter');
                break;
            case (checkStation([9, 10, 11, 12])):
                this.changeStation(_this_frame, sun, 'spring');
                break;
            case (checkStation([12, 1, 2, 3])):
                this.changeStation(_this_frame, sun, 'summer');
                break;
            default:
                break;
        }
    };

    /**
     *
     * @param _this_frame
     * @param sun
     */
    syncHours = (_this_frame, sun) => {
        const {occ_slider, occ_slider: {occ_slide}} = _this_frame;

        let
            range = this.createArray(hours.start, hours.end, 1),
            index = range.findIndex((value) => (value === this.$hours)),
            big_index = (index > -1) ? (new Big(index)) : (new Big(this.$controller.big_0)),
            big_length = (new Big(range.length)),
            big_slider_width = (new Big(occ_slider.nominalBounds.width)),
            big_slide_width = (new Big(occ_slide.nominalBounds.width)),
            big_limit = big_slide_width.div(_this.$controller.big_2),
            big_max = big_slider_width.minus(big_limit),
            big_speed = (new Big(speed));

        let big_position = big_slider_width.minus(big_limit).div(big_length).times(big_index.plus(this.$controller.big_1));

        if (this.$hours <= hours.start) {
            big_position = big_limit;
        } else if (this.$hours >= hours.end) {
            big_position = big_max;
        }

        if (big_position.gte(big_limit) && big_position.lte(big_max)) {
            this.$big_frame = _this.$controller.big_0;

            createjs.Ticker.timingMode = createjs.Ticker.RAF;

            let ticker = createjs.Ticker.on('tick', this.$functions.handleSlideHours, false, false, {
                _this_frame: _this_frame,
                sun: sun,
                data: {
                    big_slider_width: big_slider_width,
                    big_slide_width: big_slide_width,
                    big_limit: big_limit,
                    big_position: big_position,
                    big_speed: big_speed
                },
                callback: () => this.destroyTicker('_handleSlideHours')
            });

            this.setReferenceTicker('_handleSlideHours', ticker);
        }
    };

    referenceSun = (_this) => {
        return {
            object: null,

            /**
             *
             * @returns {null}
             */
            get sun() {
                return this.object;
            },

            /**
             *
             * @param object
             */
            set sun(object) {
                this.object = object;
            },

            reference() {
                const _this_frame = _this.getFrame(), {
                    occ_plant: {occ_effect, occ_effect: {occ_guide}},
                    occ_slider
                } = _this_frame;
                this.sun = {content: occ_effect, guide: occ_guide, input: occ_slider};
            },

            /**
             *
             * @param x
             */
            setCoordinateSun(x) {
                const {content, guide} = this.object;

                x = _this.toIntegerOrFloat(x);

                if (_this.isIntegerOrFloat(x)) {
                    let position = _this.toInteger(x);
                    content.gotoAndStop(position);
                    guide.gotoAndStop(position);
                }
            },
            /**
             *
             * @param x
             */
            setCoordinateSlide(x) {
                const {input: {occ_slide}} = this.object;

                x = _this.toIntegerOrFloat(x);

                _this.isIntegerOrFloat(x) && occ_slide.setTransform(x, occ_slide.y);

                //occ_slide.stage.update(); RESOLVE
            },
            /**
             *
             * @param degrees
             */
            setRotation(degrees) {
                const {guide} = this.object;

                guide.setTransform(guide.x, guide.y, guide.scaleX, guide.scaleY, degrees);
            }
        }
    };

    setColors = () => {
        const
            _this_frame = this.getFrame(),
            {
                occ_slider_title,
                occ_slider,
                occ_slider_arrow
            } = _this_frame;

        occ_slider_title.occ_left.color = this.tintToHex(slide.color_title);
        occ_slider_title.occ_center.color = this.tintToHex(slide.color_title);
        occ_slider_title.occ_right.color = this.tintToHex(slide.color_title);

        for (let property of occ_slider.occ_rule.occ_line.children) {
            this.$helpersPin.setColor(property, slide.color_line);
        }

        for (let property of occ_slider.occ_rule.occ_ruler.children) {
            this.$helpersPin.setColor(property, slide.color_line);
        }

        this.$helpersPin.setColor(occ_slider_arrow, slide.color_arrow);

        this.$helpersPin.setColor(occ_slider.occ_slide.occ_round, slide.color_round);
        this.$helpersPin.setColor(occ_slider.occ_slide.occ_color, slide.color_circle);
    };

    startSun = () => {
        const
            _this_frame = this.getFrame(),
            {
                stage: {canvas},
                btn_autumn,
                btn_winter,
                btn_spring,
                btn_summer
            } = _this_frame;

        this.setColors();

        const sun = this.referenceSun(this);

        sun.reference();

        const {input: {occ_slide}} = sun.sun;

        this._handleSlideMouseDown && occ_slide.off('mousedown', this._handleSlideMouseDown);
        this._handleSlideMouseDown = occ_slide.on('mousedown', this.$functions.handleSlideMouseDown, false, false, {
            _this_frame: _this_frame,
            callback: () => this.destroyTicker('_handleSlideHours')
        });

        this._handleSlidePressMove && occ_slide.off('pressmove', this._handleSlidePressMove);
        this._handleSlidePressMove = occ_slide.on('pressmove', this.$functions.handleSlidePressMove, false, false, {
            _this_frame: _this_frame,
            sun: sun
        });

        this.removeListener('touchstart', 'handle-slide-touch-touchstart', () => {
            this.addListener(canvas, 'touchstart', 'handle-slide-touch-touchstart', this.$functions.handleSlideTouch, {passive: true}, {
                _this_frame: _this_frame,
                sun: sun,
                callback: () => this.destroyTicker('_handleSlideHours')
            });
        });

        this.removeListener('touchmove', 'handle-slide-touch-touchmove', () => {
            this.addListener(canvas, 'touchmove', 'handle-slide-touch-touchmove', this.$functions.handleSlideTouch, false, {
                _this_frame: _this_frame,
                sun: sun
            });
        });

        this._handleSlideAutumn && btn_autumn.off('mousedown', this._handleSlideAutumn);
        this._handleSlideAutumn = btn_autumn.on('mousedown', this.$functions.handleSlideStationMouseDown, false, false, {
            _this_frame: _this_frame,
            sun: sun,
            occ: 'autumn'
        });

        this._handleSlideWinter && btn_winter.off('mousedown', this._handleSlideWinter);
        this._handleSlideWinter = btn_winter.on('mousedown', this.$functions.handleSlideStationMouseDown, false, false, {
            _this_frame: _this_frame,
            sun: sun,
            occ: 'winter'
        });

        this._handleSlideSpring && btn_spring.off('mousedown', this._handleSlideSpring);
        this._handleSlideSpring = btn_spring.on('mousedown', this.$functions.handleSlideStationMouseDown, false, false, {
            _this_frame: _this_frame,
            sun: sun,
            occ: 'spring'
        });

        this._handleSlideSummer && btn_summer.off('mousedown', this._handleSlideSummer);
        this._handleSlideSummer = btn_summer.on('mousedown', this.$functions.handleSlideStationMouseDown, false, false, {
            _this_frame: _this_frame,
            sun: sun,
            occ: 'summer'
        });

        this.syncHours(_this_frame, sun);

        this.syncStation(_this_frame, sun);
    };

    initSun = () => {
        this.startSun();
        this.$helpersIcon.setIcon(this.getFrame());
    };
}

/**
 *
 * @type {{}}
 */
StageSun.propTypes = {
    current_name: PropTypes.string
};

/**
 *
 * @type {{}}
 */
StageSun.defaultProps = {
    current_name: null
};

/**
 *
 * @param ClassObject
 * @param className {className: 'className'}
 * @returns {function(*): function({}=): *} pass _this ClassMain (required) and props {} (optional)
 */
export default WithType(StageSun, {className: 'StageSun'});
